import React, { useState } from 'react';
import { 
  FaShieldAlt, 
  FaCookie, 
  FaLock, 
  FaUserShield, 
  FaGavel, 
  FaBook, 
  FaFileContract, 
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';

const CookiesPrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState(null);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: <FaBook />,
      content: `Welcome to Olewin Academy ("we," "our," or "the Academy"). This Cookies Preference and Privacy Policy explains how we collect, use, store, and protect your personal information in compliance with:
      
• The Constitution of India - Article 21 (Right to Privacy) and Article 21A (Right to Education)
• The Right of Children to Free and Compulsory Education Act, 2009 (RTE Act)
• The Information Technology Act, 2000 and IT Rules, 2011
• The Digital Personal Data Protection Act, 2023 (DPDP Act)
• The Indian Contract Act, 1872
• All applicable educational regulations and guidelines issued by the Government of India

By accessing or using our website and services, you acknowledge that you have read, understood, and agree to be bound by this policy.`
    },
    {
      id: 'cookies',
      title: 'What Are Cookies?',
      icon: <FaCookie />,
      content: `Cookies are small text files stored on your device (computer, smartphone, or tablet) when you visit our website. They help us:

• Remember your preferences and settings
• Understand how you use our website
• Improve your user experience
• Ensure security of your account
• Comply with legal and regulatory requirements`
    },
    {
      id: 'cookie-types',
      title: 'Types of Cookies We Use',
      icon: <FaShieldAlt />,
      subsections: [
        {
          title: 'Essential Cookies (Strictly Necessary)',
          content: 'These cookies are required for the website to function properly and cannot be disabled. They include session management, security protection, and load balancing.'
        },
        {
          title: 'Functional Cookies',
          content: 'These cookies enable enhanced functionality like language preferences, accessibility settings, and user interface customization.'
        },
        {
          title: 'Analytics Cookies',
          content: 'These help us understand visitor interactions through Google Analytics, performance monitoring, and usage pattern analysis.'
        },
        {
          title: 'Marketing Cookies (With Consent)',
          content: 'Used only with explicit consent for advertising, social media integration, and remarketing purposes.'
        }
      ]
    },
    {
      id: 'data-collection',
      title: 'Information We Collect',
      icon: <FaFileContract />,
      subsections: [
        {
          title: 'Personal Information of Students',
          content: 'Full Name, Date of Birth, Gender, Parent/Guardian Details, Address, Email, Phone, Aadhaar (if required), Academic Records, Previous School Details, Caste/Income/Disability Certificates (if applicable), Medical Information, and Photographs.'
        },
        {
          title: 'Academic and Performance Data',
          content: 'Attendance Records, Examination Results, Assignments, Teacher Assessments, Activity Participation, Behavioral Records, and Progress Reports.'
        },
        {
          title: 'Financial Information',
          content: 'Fee Payment Details, Bank Account Information (for refunds), Scholarship Applications, and Payment Gateway Information.'
        },
        {
          title: 'Technical Information',
          content: 'IP Address, Device Information, Browser Type, Operating System, Pages Visited, and Cookie Data.'
        },
        {
          title: 'Communication Data',
          content: 'Emails, Messages, Feedback, Parent-Teacher Meeting Notes, and Grievance Records.'
        }
      ]
    },
    {
      id: 'legal-basis',
      title: 'Legal Basis for Data Processing',
      icon: <FaGavel />,
      subsections: [
        {
          title: 'Consent',
          content: 'We obtain explicit consent from students (if above 18) or parents/guardians (for minors) before collecting personal data.'
        },
        {
          title: 'Contractual Necessity',
          content: 'Processing necessary for enrollment, education services, communication, and fee collection.'
        },
        {
          title: 'Legal Obligation',
          content: 'Compliance with RTE Act 2009, Education Board Requirements, Child Protection Laws, and Regulatory Requirements.'
        },
        {
          title: 'Legitimate Interest',
          content: 'Campus security, fraud prevention, service improvement, and anonymized statistical analysis.'
        }
      ]
    },
    {
      id: 'data-usage',
      title: 'How We Use Your Information',
      icon: <FaUserShield />,
      subsections: [
        {
          title: 'Educational Purposes',
          content: 'Student admission, academic instruction, performance assessment, certificates, examinations, activities, and career guidance.'
        },
        {
          title: 'Administrative Purposes',
          content: 'Record maintenance, fee processing, attendance management, communications, admissions, and parent-teacher coordination.'
        },
        {
          title: 'Communication',
          content: 'Academic updates, event notifications, emergency information, newsletters, and query responses.'
        },
        {
          title: 'Legal and Compliance',
          content: 'RTE Act compliance, government inquiries, record maintenance, legal proceedings, and regulatory reporting.'
        },
        {
          title: 'Safety and Security',
          content: 'Campus security via CCTV, identity verification, access control, emergency response, and child protection.'
        }
      ]
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing and Disclosure',
      icon: <FaLock />,
      content: `We Share Information With:

EDUCATIONAL AUTHORITIES:
• CBSE/ICSE/State Education Boards
• University affiliations
• Government education departments
• Examination conducting bodies

REGULATORY BODIES:
• Ministry of Education, Government of India
• State and District Education Offices
• National Commission for Protection of Child Rights (NCPCR)

SERVICE PROVIDERS:
• Payment gateway providers
• SMS and email services
• Cloud storage providers
• Learning management systems
• Analytics providers

LAW ENFORCEMENT:
• When required by law or court order
• To protect rights and safety
• In response to legal proceedings

WE DO NOT SHARE FOR:
• Commercial marketing without consent
• Sale or rent of student databases
• Unauthorized advertising
• Any purpose not in this policy`
    },
    {
      id: 'data-security',
      title: 'Data Security Measures',
      icon: <FaShieldAlt />,
      subsections: [
        {
          title: 'Technical Safeguards',
          content: 'SSL/TLS encryption, encrypted storage, secure authentication, regular security audits, firewall protection, and backup procedures.'
        },
        {
          title: 'Physical Safeguards',
          content: 'Restricted data access, CCTV surveillance, secure document storage and disposal, visitor access controls.'
        },
        {
          title: 'Administrative Safeguards',
          content: 'Staff training, confidentiality agreements, data handling policies, incident response protocols, and compliance reviews.'
        }
      ]
    },
    {
      id: 'retention',
      title: 'Data Retention Period',
      icon: <FaClock />,
      content: `Student Academic Records: Permanently
Attendance Records: Minimum 5 years
Financial Records: 7 years
Medical Records: Until student turns 21
CCTV Footage: 30-90 days
Admission Applications (rejected): 1 year
Website Cookies: 6-24 months
Communication Logs: 3 years

After the retention period, data is securely deleted or anonymized.`
    },
    {
      id: 'rights',
      title: 'Your Rights Under Indian Law',
      icon: <FaCheckCircle />,
      content: `As per the Digital Personal Data Protection Act, 2023:

• Right to Access: Request and obtain copies of your data
• Right to Correction: Request correction of inaccurate data
• Right to Erasure: Request deletion (subject to legal requirements)
• Right to Data Portability: Request data in transferable format
• Right to Withdraw Consent: Withdraw consent anytime
• Right to Grievance Redressal: File complaints with our DPO
• Right to Nominate: Parents can nominate representatives
• Right to Information: Know what data is collected and how it's used`
    },
    {
      id: 'constitutional',
      title: 'Constitutional & Educational Rights',
      icon: <FaGavel />,
      content: `FUNDAMENTAL RIGHTS (Constitution of India):
• Article 21: Right to Privacy - We protect your information
• Article 21A: Right to Education - No denial based on data
• Article 14: Equality before Law - No discrimination
• Article 19(1)(a): Freedom of Speech - Feedback welcomed

RIGHT TO EDUCATION ACT, 2009:
• No capitation fee or screening during admission
• Free and compulsory education (ages 6-14, if applicable)
• No detention policy in elementary education
• Transparent admission procedures
• Grievance redressal mechanisms`
    },
    {
      id: 'children',
      title: "Children's Privacy (Special Protection)",
      icon: <FaUserShield />,
      subsections: [
        {
          title: 'Parental Consent',
          content: 'Verifiable parental/guardian consent required for children under 18. Parents can review, modify, or delete child information and limit data collection.'
        },
        {
          title: 'Child Safety',
          content: 'Compliance with POCSO Act 2012, staff background verification, child safety training, Child Protection Policy, and mandatory abuse reporting.'
        },
        {
          title: 'Limited Data Collection',
          content: 'Only essential information from minors, no marketing or profiling of children, restricted use of photographs and videos.'
        }
      ]
    },
    {
      id: 'cookie-management',
      title: 'Cookie Management',
      icon: <FaCookie />,
      content: `HOW TO MANAGE COOKIES:

Browser Settings:
• Chrome: Settings > Privacy and Security > Cookies
• Firefox: Options > Privacy & Security > Cookies
• Safari: Preferences > Privacy > Cookies
• Edge: Settings > Privacy > Cookies

Cookie Consent Tool:
Accept or reject optional cookies through our consent banner on first visit.

Opt-Out:
Google Analytics: https://tools.google.com/dlpage/gaoptout

IMPACT OF DISABLING:
• Essential cookies required for functionality
• Functional cookies enhance user experience
• Website still usable with limited cookies`
    },
    {
      id: 'breach',
      title: 'Data Breach Notification',
      icon: <FaExclamationTriangle />,
      content: `In the event of a data breach:

• Notify affected individuals within 72 hours
• Inform Data Protection Board of India
• Take immediate remedial action
• Provide protective measure guidance
• Cooperate with regulatory investigations`
    }
  ];

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <div className="bg-primary text-white py-5 my-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12 text-center">
              <FaShieldAlt className="mb-3" style={{ fontSize: '3rem' }} />
              <h1 className="display-4 fw-bold mb-3">Cookies Preference & Privacy Policy</h1>
              <h2 className="h4 mb-3">Olewin Academy</h2>
              <p className="lead mb-0">Protecting Your Privacy, Empowering Your Education</p>
              <p className="mt-2"><small>Last Updated: October 24, 2025</small></p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Banner */}
      <div className="container my-4">
        <div className="alert alert-info d-flex align-items-start" role="alert">
          <FaCheckCircle className="me-3 mt-1" style={{ fontSize: '1.5rem', flexShrink: 0 }} />
          <div>
            <h5 className="alert-heading mb-2">Your Privacy Matters</h5>
            <p className="mb-0">We are committed to protecting your personal information in accordance with Indian laws including the Digital Personal Data Protection Act, 2023, and the Right to Education Act, 2009.</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container pb-5">
        <div className="row">
          <div className="col-12">
            {/* Accordion Sections */}
            <div className="accordion" id="privacyAccordion">
              {sections.map((section, index) => (
                <div className="card border-0 shadow-sm mb-3" key={section.id}>
                  <div className="card-header bg-white border-0">
                    <button
                      className="btn btn-link text-decoration-none w-100 text-start d-flex justify-content-between align-items-center p-3"
                      onClick={() => toggleSection(section.id)}
                      style={{ color: '#0d6efd' }}
                    >
                      <div className="d-flex align-items-center">
                        <span className="me-3" style={{ fontSize: '1.5rem' }}>
                          {section.icon}
                        </span>
                        <h3 className="h5 mb-0">{section.title}</h3>
                      </div>
                      {activeSection === section.id ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                  </div>
                  {activeSection === section.id && (
                    <div className="card-body pt-0">
                      {section.subsections ? (
                        <div className="accordion" id={`subsection-${section.id}`}>
                          {section.subsections.map((subsection, subIndex) => (
                            <div className="card border mb-2" key={subIndex}>
                              <div className="card-header bg-light">
                                <h4 className="h6 mb-0">{subsection.title}</h4>
                              </div>
                              <div className="card-body">
                                <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{subsection.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ whiteSpace: 'pre-line' }}>{section.content}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Contact Information Card */}
            <div className="card border-0 shadow-sm mt-4">
              <div className="card-header bg-primary text-white">
                <h3 className="h5 mb-0 d-flex align-items-center">
                  <FaEnvelope className="me-2" />
                  Grievance Redressal & Contact Information
                </h3>
              </div>
              <div className="card-body">
                <div className="row g-4">
                  <div className="col-md-6">
                    <h4 className="h6 fw-bold mb-3">Data Protection Officer (DPO)</h4>
                    <div className="mb-2">
                      <FaUserShield className="me-2 text-primary" />
                      <strong>Name:</strong> [DPO Name]
                    </div>
                    <div className="mb-2">
                      <FaEnvelope className="me-2 text-primary" />
                      <strong>Email:</strong> privacy@olewinacademy.com
                    </div>
                    <div className="mb-2">
                      <FaPhone className="me-2 text-primary" />
                      <strong>Phone:</strong> [Contact Number]
                    </div>
                    <div className="mb-2">
                      <FaClock className="me-2 text-primary" />
                      <strong>Hours:</strong> Mon-Fri, 9:00 AM - 5:00 PM
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h4 className="h6 fw-bold mb-3">Olewin Academy</h4>
                    <div className="mb-2">
                      <FaMapMarkerAlt className="me-2 text-primary" />
                      <strong>Address:</strong> [Academy Address]
                    </div>
                    <div className="mb-2">
                      <FaEnvelope className="me-2 text-primary" />
                      <strong>Email:</strong> info@olewinacademy.com
                    </div>
                    <div className="mb-2">
                      <FaPhone className="me-2 text-primary" />
                      <strong>Phone:</strong> [Contact Number]
                    </div>
                    <div className="mb-2">
                      <FaClock className="me-2 text-primary" />
                      <strong>Office Hours:</strong> Mon-Fri 8AM-4PM, Sat 8AM-12PM
                    </div>
                  </div>
                </div>
                
                <div className="alert alert-warning mt-4" role="alert">
                  <h5 className="alert-heading mb-2">
                    <FaExclamationTriangle className="me-2" />
                    Grievance Procedure
                  </h5>
                  <ol className="mb-0 ps-3">
                    <li>Submit complaint via email or post</li>
                    <li>Acknowledgment within 48 hours</li>
                    <li>Resolution within 30 days</li>
                    <li>If unsatisfied, approach Data Protection Board of India</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Consent Statement */}
            <div className="card border-0 shadow-sm mt-4 bg-light">
              <div className="card-body">
                <h3 className="h5 mb-3 d-flex align-items-center">
                  <FaCheckCircle className="me-2 text-success" />
                  Consent and Agreement
                </h3>
                <p className="mb-3">By enrolling your child or accessing our services, you confirm that:</p>
                <ul className="mb-3">
                  <li>You have read and understood this policy</li>
                  <li>You consent to collection and use of data as described</li>
                  <li>You are the parent/legal guardian (for minors under 18)</li>
                  <li>Information provided is accurate and complete</li>
                  <li>You will inform us of any changes to the information</li>
                </ul>
                <div className="alert alert-info mb-0">
                  <strong>For Minors (Under 18 years):</strong> Parent/Guardian consent is mandatory.<br />
                  <strong>For Adults (18 years and above):</strong> Direct consent is obtained.
                </div>
              </div>
            </div>

            {/* Governing Law */}
            <div className="card border-0 shadow-sm mt-4">
              <div className="card-body">
                <h3 className="h5 mb-3 d-flex align-items-center">
                  <FaGavel className="me-2 text-primary" />
                  Governing Law and Jurisdiction
                </h3>
                <p className="mb-2">This policy is governed by:</p>
                <ul className="mb-3">
                  <li>The Constitution of India</li>
                  <li>The Digital Personal Data Protection Act, 2023</li>
                  <li>The Information Technology Act, 2000</li>
                  <li>The Right to Education Act, 2009</li>
                  <li>All applicable central and state laws</li>
                </ul>
                <p className="mb-0"><strong>Jurisdiction:</strong> All disputes are subject to the exclusive jurisdiction of courts in [Your City/State], India.</p>
              </div>
            </div>

            {/* Footer Note */}
            <div className="text-center mt-4">
              <p className="text-muted small mb-0">
                <FaLock className="me-2" />
                This policy is designed to comply with all applicable Indian laws and regulations.
                We recommend periodic legal review to ensure continued compliance.
              </p>
              <p className="text-muted small mt-2 mb-0">
                <strong>Document Version:</strong> 1.0 | <strong>Effective Date:</strong> October 24, 2025
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Back to Top Button */}
      <button
        className="btn btn-primary rounded-circle position-fixed shadow"
        style={{ bottom: '2rem', right: '2rem', width: '3rem', height: '3rem', zIndex: 1000 }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <FaChevronUp />
      </button>

      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />
    </div>
  );
};

export default CookiesPrivacyPolicy;